import {
  Layer,
  TableBatchAction,
  TableBatchActions,
  TableBatchActionsProps,
  TableToolbar,
  TableToolbarContent,
} from "@carbon/react";
import { SingleSelectAppliedFilter, ToolbarProps } from "@flaminem/ui-components";
import { useEffect } from "react";

import { AppliedFilters } from "src/components/Filters/AppliedFilters.tsx";
import { useT } from "src/internationalization";
import { AlertSummary } from "src/types/alert";

import { AlertsAndExecutionsFilter } from "./AlertsAndExecutionsFilter";
import { AlertsAndExecutions } from "./useAlertsAndExecutionsTabData";

type AlertsAndExecutionsToolbarProps = {
  setIsDisplayExecutionButton: (displayExecutionButton: boolean) => void;
  values: ToolbarProps;
  getBatchActionProps: () => {
    onCancel: () => void;
    onSelectAll?: () => void | undefined;
    shouldShowBatchActions: boolean;
    totalCount: number;
    totalSelected: number;
  };
  alertsAndExecutions: AlertsAndExecutions[];
  alertsMap: Map<string, AlertSummary>;
  onCompareVersions: (selectedIndexes: number[]) => void;
  onCompareExecutions: (selectedIndexes: number[]) => void;
  onlyOpenAlerts: boolean;
  onOnlyOpenAlertsChange: (value: boolean) => void;
};

export const AlertsAndExecutionsToolbar: React.FC<AlertsAndExecutionsToolbarProps> = ({
  setIsDisplayExecutionButton,
  values,
  getBatchActionProps,
  alertsAndExecutions,
  alertsMap,
  onCompareVersions,
  onCompareExecutions,
  onlyOpenAlerts,
  onOnlyOpenAlertsChange,
}) => {
  const t = useT();

  const batchActionProps = getBatchActionProps();
  useEffect(
    () => setIsDisplayExecutionButton(!batchActionProps.shouldShowBatchActions),
    [batchActionProps.shouldShowBatchActions, setIsDisplayExecutionButton]
  );

  const hasManualAlertWithoutSnapshots = () =>
    values.selectedRowsIndexes.some((index) => {
      const item = alertsAndExecutions[index];
      if (item.scenario === "Manual") {
        // Check if the alert has snapshots
        const alert = alertsMap.get(item.id);
        return !alert?.creationCase;
      }
      return false;
    });

  const translateWithId: TableBatchActionsProps["translateWithId"] = (id, args) => {
    if (id === "carbon.table.batch.cancel") {
      return t("page.case.tab.alerts.and.executions.table.cancel");
    }
    if (id === "carbon.table.batch.selectAll") {
      return t("page.case.tab.alerts.and.executions.table.selected.all");
    }
    if (id === "carbon.table.batch.item.selected") {
      return t("page.case.tab.alerts.and.executions.table.item.selected", {
        item: args?.totalSelected,
      });
    }

    return t("page.case.tab.alerts.and.executions.table.items.selected", {
      items: args?.totalSelected,
    });
  };

  const onResetFilter = () => onOnlyOpenAlertsChange(false);

  return (
    <>
      <TableToolbar aria-label="data table toolbar">
        <TableToolbarContent>
          <AlertsAndExecutionsFilter
            values={{ onlyOpenAlerts }}
            onApplyFilter={(filters) => onOnlyOpenAlertsChange(filters.onlyOpenAlerts)}
            onResetFilter={onResetFilter}
          />
        </TableToolbarContent>
        <TableBatchActions
          {...batchActionProps}
          onSelectAll={undefined}
          translateWithId={translateWithId}
        >
          <TableBatchAction
            disabled={batchActionProps.totalSelected !== 2 || hasManualAlertWithoutSnapshots()}
            renderIcon={() => <></>}
            onClick={() => onCompareExecutions(values.selectedRowsIndexes)}
          >
            <div> {t("page.case.tab.alerts.and.executions.table.compare.executions")}</div>
          </TableBatchAction>
          <TableBatchAction
            disabled={batchActionProps.totalSelected !== 2 || hasManualAlertWithoutSnapshots()}
            renderIcon={() => <></>}
            onClick={() => onCompareVersions(values.selectedRowsIndexes)}
          >
            {t("page.case.tab.alerts.and.executions.table.compare.versions")}
          </TableBatchAction>
        </TableBatchActions>
      </TableToolbar>
      {onlyOpenAlerts && (
        <div className="flex w-full bg-layer">
          <Layer className="p-5">
            <AppliedFilters onClear={onResetFilter}>
              <SingleSelectAppliedFilter
                item={{
                  label: t("page.case.tab.alerts.and.executions.filter.onlyOpenAlerts"),
                  value: t("page.case.tab.alerts.and.executions.filter.onlyOpenAlerts"),
                }}
                title={t("page.case.tab.alerts.and.executions.filter.onlyOpenAlerts")}
                onClear={onResetFilter}
              />
            </AppliedFilters>
          </Layer>
        </div>
      )}
    </>
  );
};
